// Opening balances from Xero (M6 plan D1): upload the trial balance exported to Excel, check every
// account is matched, then post the opening entry and lock the cutover date.
import { useState } from 'react';
import type { Api } from '../api.js';
import { dollars } from '../format.js';

interface Preview {
  asAt: string;
  total: string;
  problems: string[];
  lines: {
    code: string | null;
    name: string;
    amount: string;
    account: { code: string; name: string } | null;
  }[];
}

async function base64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export function OpeningBalances({ api, orgId }: { api: Api; orgId: string }) {
  const [file, setFile] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function choose(f: File | undefined) {
    if (!f) return;
    setError('');
    setDone('');
    setPreview(null);
    setBusy(true);
    try {
      const content = await base64(f);
      setFile(content);
      setPreview(
        await api.send<Preview>(orgId, 'POST', '/opening-balances/preview', { file: content }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the file.');
    } finally {
      setBusy(false);
    }
  }
  async function post() {
    setBusy(true);
    setError('');
    try {
      const r = await api.send<{ lockDate: string }>(orgId, 'POST', '/opening-balances', { file });
      setDone(
        `Opening balances posted. The books are locked up to ${r.lockDate}: before that date, Xero is the record.`,
      );
      setPreview(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not post the opening balances.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack">
      <h1>Opening balances from Xero</h1>
      <p className="muted">
        In Xero: Reports, Trial Balance, set the date to the cutover (30 September 2026), Update,
        Export, Excel. Upload that file here.
      </p>
      <label className="button-like">
        {busy ? 'Reading' : 'Choose the trial balance (.xlsx)'}
        <input
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => void choose(e.target.files?.[0])}
        />
      </label>
      {preview && (
        <>
          <table className="table">
            <caption>
              Trial balance at {preview.asAt}: {dollars(preview.total)} each side
            </caption>
            <thead>
              <tr>
                <th>Xero account</th>
                <th>Foderis account</th>
                <th className="num">Debit (+) / credit (-)</th>
              </tr>
            </thead>
            <tbody>
              {preview.lines.map((l) => (
                <tr key={`${l.code}-${l.name}`} className={l.account ? undefined : 'error'}>
                  <td>
                    {l.code ? `${l.code} ` : ''}
                    {l.name}
                  </td>
                  <td>{l.account ? `${l.account.code} ${l.account.name}` : 'Not found'}</td>
                  <td className="num">{dollars(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.problems.map((p) => (
            <p key={p} className="error">
              {p}
            </p>
          ))}
          {preview.problems.length === 0 && (
            <button type="button" disabled={busy} onClick={post}>
              Post the opening balances and lock {preview.asAt}
            </button>
          )}
        </>
      )}
      {done && <p>{done}</p>}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
